import type { AccountRefDto, LibraryEntryDto, LibraryGround, LibraryRefDto } from '@bk/contracts';
import { useState } from 'react';
import { useTenantQuery } from '../../../lib/useTenant';

/**
 * Da dove nasce una voce dell'azienda ideale (ADR 0130): sotto la voce, in piccolo, le voci della libreria in parole
 * semplici («Da INAPP, Atlante del Lavoro: …», con la licenza quando è non commerciale); al tocco la scheda della voce.
 * Senza riferimenti dice se vale per una norma verificata o se la fonte è da verificare.
 */
export function LibrarySources({ refs, fondata, conti, className = '' }: { refs?: LibraryRefDto[] | null; fondata?: LibraryGround | null; conti?: AccountRefDto[] | null; className?: string }) {
  const [open, setOpen] = useState<string | null>(null);
  const list = refs ?? [];
  // Il numero accanto alla voce (ADR 0132): i conti del bilancio da cui nasce, con l'importo e l'anno.
  const accounts = conti?.length ? (
    <p className={`mt-1 text-[11px] leading-snug text-ink/55 ${className}`}>
      Dai conti:{' '}
      {conti.map((c, i) => (
        <span key={c.codice}>
          {i > 0 && '; '}
          {c.descrizione.charAt(0) + c.descrizione.slice(1).toLowerCase()} <span className="font-medium tabular-nums text-ink/75">{money(c.importo)}</span> ({c.chiuso_il.slice(0, 4)})
        </span>
      ))}
    </p>
  ) : null;
  if (!list.length) {
    if (accounts) return accounts;
    if (fondata === 'norma') return <p className={`mt-1 text-[11px] text-ink/40 ${className}`}>Da una norma verificata nel testo ufficiale</p>;
    if (fondata === 'nessuna') return <p className={`mt-1 text-[11px] text-amber-700/80 ${className}`}>Fonte da verificare: non nasce da una voce della libreria</p>;
    return null;
  }
  // Le voci della stessa fonte su una riga: «Da ISTAT, CP2021: 2.5.1.2.0 Specialisti…; 3.3.1.2.1 Tecnici…».
  const bySource = new Map<string, LibraryRefDto[]>();
  for (const r of list) bySource.set(r.fonte, [...(bySource.get(r.fonte) ?? []), r]);
  return (
    <div className={`mt-1 space-y-0.5 text-[11px] leading-snug text-ink/45 ${className}`}>
      {accounts}
      {[...bySource].map(([fonte, rs]) => (
        <p key={fonte}>
          Da {fonte}:{' '}
          {rs.map((r, i) => (
            <span key={r.chiave}>
              {i > 0 && '; '}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(open === r.chiave ? null : r.chiave);
                }}
                className="text-left underline decoration-ink/20 underline-offset-2 hover:text-ink/70 hover:decoration-ink/50"
                aria-expanded={open === r.chiave}
              >
                {r.codice} {r.titolo}
              </button>
            </span>
          ))}
          {rs[0]!.non_commerciale && <span className="text-ink/35"> — {shortLicence(rs[0]!.licenza)}, uso non commerciale</span>}
        </p>
      ))}
      {open && <EntryCard chiave={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

const money = (n: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

const shortLicence = (l: string | null) => (l ? (/CC BY-NC[^\s(—]*\s*[\d.]*/.exec(l)?.[0]?.trim() ?? l.split(/[—(]/)[0]!.trim()) : 'licenza non commerciale');

/** La scheda di una voce della libreria, letta al tocco. */
function EntryCard({ chiave, onClose }: { chiave: string; onClose: () => void }) {
  const q = useTenantQuery<LibraryEntryDto>(['libreria', 'voce', chiave], `/standards/voce?chiave=${encodeURIComponent(chiave)}`);
  const [all, setAll] = useState(false);
  const e = q.data;
  return (
    <div className="mt-1.5 rounded-xl border border-ink/10 bg-white p-3 text-[12px] text-ink/70 shadow-sm" onClick={(ev) => ev.stopPropagation()}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold text-ink/85">{e ? `${e.codice} ${e.titolo}` : chiave}</p>
        <button type="button" onClick={onClose} className="shrink-0 text-ink/40 hover:text-ink/70" aria-label="Chiudi la scheda">
          ✕
        </button>
      </div>
      {q.isLoading && <p className="mt-1 text-ink/40">Leggo la scheda…</p>}
      {q.isError && <p className="mt-1 text-ink/40">La scheda non si legge ora.</p>}
      {e && (
        <>
          <p className="mt-0.5 text-[11px] text-ink/45">{e.tipo}</p>
          {e.ramo.length > 0 && <p className="mt-1 text-[11px] text-ink/50">{e.ramo.map((x) => `${x.codice} ${x.titolo}`).join(' › ')}</p>}
          {e.descrizione && (
            <p className="mt-1.5 whitespace-pre-line">
              {all || e.descrizione.length <= 500 ? e.descrizione : `${e.descrizione.slice(0, 480)}…`}
              {e.descrizione.length > 500 && (
                <button type="button" onClick={() => setAll(!all)} className="ml-1 text-[11px] text-ink/45 underline">
                  {all ? 'meno' : 'tutto'}
                </button>
              )}
            </p>
          )}
          {e.dettagli.length > 0 && (
            <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-[11px] text-ink/55">
              {e.dettagli.map((d) => (
                <li key={d}>{d}</li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[11px] text-ink/45">
            Fonte: {e.attribuzione ?? e.fonte}
            {e.versione ? ` (${e.versione})` : ''}
            {e.url && /^https?:\/\//.test(e.url) && (
              <>
                {' — '}
                <a href={e.url} target="_blank" rel="noreferrer" className="underline">
                  sito della fonte
                </a>
              </>
            )}
          </p>
          {e.licenza && <p className="mt-0.5 text-[11px] text-ink/40">Licenza: {e.licenza}</p>}
        </>
      )}
    </div>
  );
}
